/**
 * Integração Tique Taque (E9-API F4 — plano-integracao-tiquetaque-api.md §3.5/§5):
 * configuração do token da API pública v2.1 e histórico das corridas de sync.
 * Precedente: IntegracaoConveniaPage.
 *
 * - Status: token configurado? (origem banco × env) e última corrida;
 * - Token: PUT /integracoes/tiquetaque/config — o valor NUNCA é exibido de
 *   volta (campo sempre vazio; placeholder "•••" quando já configurado) e não
 *   vai para a auditoria; o valor do banco tem precedência sobre o do ambiente;
 * - Histórico: corridas de TODAS as competências (auditoria tiquetaque_syncs)
 *   com detalhe expansível: contagens por efeito, importações criadas, alertas
 *   e reconciliação do banco de horas por pessoa (hh:mm).
 *
 * A sincronização em si acontece no cartão "Ponto" da página da competência
 * (botão "Sincronizar do Tique Taque") — esta página é configuração/auditoria.
 */
import { Fragment, useEffect, useState } from "react";
import { Alert, Anchor, Badge, Button, Card, Collapse, Group, PasswordInput, Table, Text, Title } from "@mantine/core";
import { IconChevronDown, IconChevronRight, IconPlugConnected } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia, fmtData, fmtDataHora } from "../format";
import { notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { TabelaDados, Th, TdNum } from "../componentes/listas";
import {
  BadgeStatusSync,
  BlocoReconciliacao,
  rotuloEfeitos,
  type StatusTiqueTaque,
  type SyncTiqueTaque,
} from "../tiquetaque-comum";

export function IntegracaoTiqueTaquePage() {
  const [status, setStatus] = useState<StatusTiqueTaque | null>(null);
  const [syncs, setSyncs] = useState<SyncTiqueTaque[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [token, setToken] = useState("");
  const [salvandoToken, setSalvandoToken] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);

  async function carregar() {
    try {
      const [s, h] = await Promise.all([
        api.get<StatusTiqueTaque>("/integracoes/tiquetaque/status"),
        api.get<{ syncs: SyncTiqueTaque[] }>("/integracoes/tiquetaque/syncs"),
      ]);
      setStatus(s);
      setSyncs(h.syncs);
    } catch (e) {
      setErro(e);
    }
  }
  useEffect(() => {
    void carregar();
  }, []);

  async function salvarToken() {
    try {
      setSalvandoToken(true);
      await api.put("/integracoes/tiquetaque/config", { token: token.trim() });
      notificarOk("Token do Tique Taque cadastrado.");
      setToken("");
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setSalvandoToken(false);
    }
  }

  if (erro) {
    return (
      <>
        <Title order={2} mb="md">
          Integração Tique Taque
        </Title>
        <Alert color="red" title="Falha ao carregar">
          {String(erro)}
        </Alert>
      </>
    );
  }
  if (!status || !syncs) return <Carregando linhas={6} />;

  const ultimo = syncs[0] ?? null;

  return (
    <>
      <Title order={2} mb={4}>
        Integração Tique Taque
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        Importação automática do ponto pela API pública v2.1 (somente leitura): espelhos (horas extras e adicional
        noturno), banco de horas com saldo reconciliado e afastamentos. O sync roda por competência no cartão "Ponto"
        da página dela (botão "Sincronizar do Tique Taque"); os CSVs continuam valendo como contingência.
      </Text>

      <Card withBorder shadow="xs" mb="md">
        <Group justify="space-between" mb="sm" wrap="wrap">
          <Title order={5}>Status</Title>
          <Badge color={status.configurado ? "sillis" : "red"} variant="light" leftSection={<IconPlugConnected size={12} />}>
            {status.configurado ? `token configurado (${status.origem})` : "token NÃO configurado"}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed">
          {ultimo
            ? `Último sync: ${fmtDataHora(ultimo.inicioEm)} — competência ${fmtCompetencia(ultimo.competenciaId)}, ` +
              `${ultimo.status === "concluido" && ultimo.resumo ? rotuloEfeitos(ultimo.resumo) : ultimo.status}` +
              (ultimo.erro ? ` (${ultimo.erro})` : "")
            : "Nenhum sync executado ainda."}
        </Text>
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Token da API
        </Title>
        <Group align="flex-end" wrap="wrap">
          <PasswordInput
            label={status.configurado ? "Substituir token (o atual nunca é exibido)" : "Token do Tique Taque"}
            placeholder={status.configurado ? "••••••••••••••••" : "Cole o token do TiqueAdmin (seção Integrações)"}
            value={token}
            onChange={(e) => setToken(e.currentTarget.value)}
            w={420}
          />
          <Button onClick={() => void salvarToken()} loading={salvandoToken} disabled={!token.trim()}>
            Salvar token
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          Somente leitura (GET) nesta fase. O valor fica no banco local (com precedência sobre a variável de ambiente
          TIQUETAQUE_TOKEN) e nunca sai pela API nem vai para a auditoria.
        </Text>
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Histórico de syncs
        </Title>
        {syncs.length === 0 ? (
          <Vazio texto="Nenhum sync executado — ele roda no cartão &quot;Ponto&quot; da página da competência." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th></Th>
                <Th>Quando</Th>
                <Th>Competência</Th>
                <Th>Período de ponto</Th>
                <Th>Status</Th>
                <Th num>Resumo</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {syncs.map((s) => {
                const expandido = aberto === s.id;
                return (
                  <Fragment key={s.id}>
                    <Table.Tr
                      onClick={() => setAberto(expandido ? null : s.id)}
                      style={{ cursor: "pointer" }}
                      bg={expandido ? "gray.0" : undefined}
                    >
                      <Table.Td w={28}>{expandido ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}</Table.Td>
                      <Table.Td>
                        <Text size="sm">{fmtDataHora(s.inicioEm)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Anchor href={`#/competencias/${s.competenciaId}`} size="sm" onClick={(e) => e.stopPropagation()}>
                          {fmtCompetencia(s.competenciaId)}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {fmtData(s.escopo.periodo.inicioIso)} a {fmtData(s.escopo.periodo.fimIso)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <BadgeStatusSync status={s.status} />
                      </Table.Td>
                      <TdNum>
                        <Text size="xs" component="span">
                          {s.resumo ? rotuloEfeitos(s.resumo) : (s.erro ?? "—")}
                        </Text>
                      </TdNum>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td colSpan={6} p={0}>
                        <Collapse expanded={expandido}>
                          <div style={{ padding: 12 }}>
                            {s.erro && (
                              <Text size="sm" c="red.7" mb={4}>
                                Erro: {s.erro}
                              </Text>
                            )}
                            {s.resumo && (
                              <>
                                <Text size="sm" mb={4}>
                                  <strong>Efeitos:</strong> {rotuloEfeitos(s.resumo)} ·{" "}
                                  {s.resumo.importacoes.filter((i) => i.criada).length} importação(ões) criada(s)
                                  {s.resumo.importacoes.every((i) => !i.criada) ? " (re-sync idempotente — sem alterações)" : ""}
                                </Text>
                                <BlocoReconciliacao
                                  resumo={s.resumo.reconciliacaoBanco}
                                  detalhe={s.resumo.reconciliacaoBancoDetalhe}
                                />
                                {s.resumo.alertas.length > 0 && (
                                  <>
                                    <Text size="sm" fw={700} mt="sm" mb={2}>
                                      Alertas ({s.resumo.alertas.length}) — não bloqueantes:
                                    </Text>
                                    {s.resumo.alertas.map((a, i) => (
                                      <Text key={i} size="xs" c="yellow.8" ml="md">
                                        • {a}
                                      </Text>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </Collapse>
                      </Table.Td>
                    </Table.Tr>
                  </Fragment>
                );
              })}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
    </>
  );
}
